import React, { useEffect, useState } from "react";
import { endpoints } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff, Clock, ShieldAlert, Check as CheckIcon, X } from "lucide-react";

/**
 * WellnessAccessPanel — client-side control surface for Wellness Practice ↔ Client photo
 * consents. Lets the member see who has asked for permission to send
 * Before/After procedure photos, accept or deny pending requests, and
 * revoke any active grant at any time.
 *
 * The client is always in control. The spa can never send without an
 * ``active`` consent stamped by the backend; the frontend simply mirrors
 * that state and offers the actions.
 */
export default function WellnessAccessPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({}); // id -> boolean

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await endpoints.listClientSpaConsents("all");
      setItems(data || []);
    } catch {
      setItems([]);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const act = async (id, verb) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      const fn = verb === "accept" ? endpoints.acceptSpaConsent
        : verb === "deny" ? endpoints.denySpaConsent
        : endpoints.revokeSpaConsent;
      await fn(id);
      const msg = verb === "accept" ? "Access granted." : verb === "deny" ? "Request declined." : "Access revoked.";
      toast.success(msg);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update this request.");
    }
    setBusy((b) => ({ ...b, [id]: false }));
  };

  const pending = items.filter((i) => i.status === "pending");
  const active = items.filter((i) => i.status === "active");
  const past = items.filter((i) => i.status === "denied" || i.status === "revoked");

  return (
    <section
      className="p-8 border hairline rounded-md bg-[hsl(var(--paper))] reveal reveal-3"
      data-testid="wellness-access-panel"
    >
      <div className="text-[11px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Wellness access</div>
      <div className="mt-3">
        <div className="font-display text-3xl">Who can send you photos</div>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 max-w-2xl">
          Your Wellness code alone is never enough. A wellness practice can only send Before &amp; After photos into your Tracking tab
          after <em>you</em> accept their request here. You can revoke access at any time — future sends stop the
          moment you do.
        </p>
      </div>

      {loading ? (
        <div className="mt-6 text-sm text-[hsl(var(--muted-foreground))]" data-testid="wellness-access-loading">
          Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="mt-6 text-sm text-[hsl(var(--muted-foreground))]" data-testid="wellness-access-empty">
          No wellness practices have asked yet. When one does, you'll see them here with clear Accept / Deny controls.
        </div>
      ) : (
        <div className="mt-6 space-y-6">
          {pending.length > 0 && (
            <ConsentGroup
              testid="wellness-access-pending"
              label="Pending requests"
              icon={<Clock size={14} className="text-[hsl(var(--clay))]" />}
              items={pending}
              renderActions={(c) => (
                <div className="flex items-center gap-2 flex-wrap">
                  <Button size="sm" disabled={!!busy[c.id]} onClick={() => act(c.id, "accept")} data-testid={`wellness-access-accept-${c.id}`} className="rounded-full bg-[hsl(var(--moss))] hover:bg-[hsl(var(--moss))]/90 text-[hsl(var(--paper))]">
                    <CheckIcon size={14} className="mr-1.5" /> Accept
                  </Button>
                  <Button size="sm" variant="outline" disabled={!!busy[c.id]} onClick={() => act(c.id, "deny")} data-testid={`wellness-access-deny-${c.id}`} className="rounded-full">
                    <X size={14} className="mr-1.5" /> Deny
                  </Button>
                </div>
              )}
            />
          )}
          {active.length > 0 && (
            <ConsentGroup
              testid="wellness-access-active"
              label="Active"
              icon={<ShieldCheck size={14} className="text-[hsl(var(--moss))]" />}
              items={active}
              renderActions={(c) => (
                <Button size="sm" variant="outline" disabled={!!busy[c.id]} onClick={() => act(c.id, "revoke")} data-testid={`wellness-access-revoke-${c.id}`} className="rounded-full">
                  <ShieldOff size={14} className="mr-1.5" /> Revoke
                </Button>
              )}
            />
          )}
          {past.length > 0 && (
            <ConsentGroup
              testid="wellness-access-past"
              label="Past"
              icon={<ShieldAlert size={14} className="text-[hsl(var(--muted-foreground))]" />}
              items={past}
              renderActions={() => null}
            />
          )}
        </div>
      )}
    </section>
  );
}

function ConsentGroup({ label, icon, items, renderActions, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))] inline-flex items-center gap-2">
        {icon} {label} · {items.length}
      </div>
      <ul className="mt-3 divide-y hairline">
        {items.map((c) => (
          <li
            key={c.id}
            className="py-4 flex items-start justify-between gap-4 flex-wrap"
            data-testid={`wellness-access-item-${c.id}`}
          >
            <div className="min-w-0 max-w-lg">
              <div className="font-display text-lg leading-tight">{c.spa_name}</div>
              {c.note && (
                <div className="text-sm text-[hsl(var(--muted-foreground))] mt-1 italic">"{c.note}"</div>
              )}
              <div className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mt-2">
                {c.status === "pending" ? "Requested " : c.status === "revoked" ? "Revoked " : c.status === "denied" ? "Declined " : "Granted "}
                {relTime(c.responded_at || c.revoked_at || c.requested_at)}
              </div>
            </div>
            {renderActions(c)}
          </li>
        ))}
      </ul>
    </div>
  );
}

function relTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return ""; }
}
